import {Text} from '@mantine/core';
import React, {useState} from 'react';

import ArchiveModal from 'archive/components/modals/ArchiveModal';
import SkillDetailModal from 'llm/chat-library/skills/SkillDetailModal';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

interface SkillsListProps {
  skills: Array<SkillSummary>;
  isTenantAdmin: boolean;
  onArchive: (skillId: string, reason: string) => void;
}

function SkillCard({
  skill,
  isTenantAdmin,
  onArchiveClick,
  onOpenDetail,
}: {
  skill: SkillSummary;
  isTenantAdmin: boolean;
  onArchiveClick: (skill: SkillSummary) => void;
  onOpenDetail: (skill: SkillSummary) => void;
}): JSX.Element {
  // Store-installed skills are managed via the TAC extensions page (uninstall), not archived directly.
  const canArchive = skill.extensionDefinitionId == null && (skill.isOwnedByCurrentUser || isTenantAdmin);

  return (
    <div
      aria-label={`View details for ${skill.name}`}
      role="button"
      tabIndex={0}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px 10px',
        borderRadius: '6px',
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
      }}
      onClick={() => {
        onOpenDetail(skill);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onOpenDetail(skill);
        }
      }}
    >
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
            <Text fw="bold" size="sm" truncate={true}>
              {skill.name}
            </Text>
          </div>
          {skill.creatorName && (
            <Text size="xs" c="dimmed">
              by {skill.creatorName}
            </Text>
          )}
          <Text size="xs" c="dimmed" lineClamp={2} style={{marginTop: 2}}>
            {skill.description}
          </Text>
        </div>
        {canArchive && (
          <div style={{flexShrink: 0, marginTop: '2px'}}>
            <Button
              buttonStyle={ButtonStyle.TEXT}
              icon="archive"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onArchiveClick(skill);
              }}
              size={ButtonSize.SMALL}
              dataTestAttrs={{component: 'SkillsList', element: 'archiveSkillButton'}}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({title}: {title: string}): JSX.Element {
  return (
    <Text fw="bold" size="xs" c="dimmed" style={{textTransform: 'uppercase', letterSpacing: '0.5px'}}>
      {title}
    </Text>
  );
}

export default function SkillsList({skills, isTenantAdmin, onArchive}: SkillsListProps): JSX.Element {
  const [detailSkill, setDetailSkill] = useState<SkillSummary | null>(null);

  if (skills.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No skills available yet.
      </Text>
    );
  }

  const mySkills = skills.filter((s) => s.isOwnedByCurrentUser);
  const sharedWithMe = skills.filter((s) => !s.isOwnedByCurrentUser);

  function handleArchiveClick(skill: SkillSummary): void {
    legacyRunModal(ArchiveModal, {
      items: [{id: skill.id, name: skill.name}],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        for (const id of itemIds) {
          onArchive(id, purpose);
        }
      },
    });
  }

  return (
    <>
      <SkillDetailModal
        skill={detailSkill}
        opened={detailSkill != null}
        onClose={() => {
          setDetailSkill(null);
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {mySkills.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
            <SectionHeader title="My Skills" />
            {mySkills.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                isTenantAdmin={isTenantAdmin}
                onArchiveClick={handleArchiveClick}
                onOpenDetail={setDetailSkill}
              />
            ))}
          </div>
        )}
        {sharedWithMe.length > 0 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
            <SectionHeader title="Shared with Me" />
            {sharedWithMe.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                isTenantAdmin={isTenantAdmin}
                onArchiveClick={handleArchiveClick}
                onOpenDetail={setDetailSkill}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
